import actionTypes from 'constants/actionTypes'
import defaultsDeep from 'lodash/defaultsDeep'
import { isHardFail } from 'lib/action'

interface NodeDataState {
  content: string
  kind: string
  readers: unknown[]
  writers: unknown[]
  creator: unknown
}

interface NodeState {
  data: NodeDataState
  syncData: NodeDataState | null
  local: boolean
  isFetching: boolean
  deleted: boolean
  failed: boolean
  id: string
  modified: number | null
  initialPosition?: unknown
}

interface NodeInput {
  id?: string
  content?: string
  kind?: string
  readers?: unknown[]
  writers?: unknown[]
  creator?: unknown
  modified?: string | number
  [key: string]: unknown
}

interface NodeAction {
  type?: string
  payload?: {
    content?: string
    kind?: string
    readers?: unknown[]
    writers?: unknown[]
    creator?: unknown
    position?: unknown
    data?: NodeInput
  }
  meta?: {
    isRetry?: boolean
    previousAction?: {
      meta?: {
        receivedAt?: number
      }
    }
  }
}

const initialState: NodeState = {
  data: {
    content: '',
    kind: '',
    readers: [],
    writers: [],
    creator: null
  },
  syncData: null,
  local: false,
  isFetching: false,
  deleted: false,
  failed: false,
  id: '',
  modified: null,
}


const single = (state: NodeState = initialState, action: NodeAction, node: NodeInput = {}): NodeState => {
  state.id = node.id ?? state.id
  switch (action.type) {
    case actionTypes.GRAPH.GET_NEIGHBORHOOD.RECEIVE: {
      if (state.isFetching) return state
      if (state.modified && (action.meta?.previousAction?.meta?.receivedAt ?? 0) < state.modified) return state
      const data = defaultsDeep(
        {
          content: node.content,
          kind: node.kind,
          readers: node.readers,
          writers: node.writers,
          creator: node.creator
        },
        state.data
      )
      return defaultsDeep(
        {
          syncData: data,
          data: data,
          isFetching: false,
          deleted: false,
          local: false,
          modified: new Date(node.modified ?? Date.now()).getTime() || Date.now()
        },
        state
      )
    }
    case actionTypes.NODES.ONE.CREATE.REQUEST: {
      let data: Partial<NodeDataState> = {}
      if (typeof action.payload?.content === 'string') data.content = action.payload.content
      if (typeof action.payload?.kind === 'string') data.kind = action.payload.kind
      if (Array.isArray(action.payload?.readers)) data.readers = action.payload.readers
      if (Array.isArray(action.payload?.writers)) data.writers = action.payload.writers
      if (action.payload?.creator !== undefined) data.creator = action.payload.creator
      if (action.meta?.isRetry) data = {}
      return defaultsDeep(
        {
          initialPosition: action.payload?.position,
          data: data,
          local: true,
          isFetching: true,
          deleted: false,
          modified: Date.now()
        },
        state
      )
    }
    case actionTypes.NODES.ONE.CREATE.RECEIVE:
    case actionTypes.NODES.ONE.CREATE.NOTIFY: {
      const payloadData = action.payload?.data
      const data = defaultsDeep(
        {
          content: payloadData?.content,
          kind: payloadData?.kind,
          readers: payloadData?.readers,
          writers: payloadData?.writers,
          creator: payloadData?.creator
        },
        state.data
      )
      return defaultsDeep(
        {
          syncData: data,
          data: data,
          local: false,
          isFetching: false,
          deleted: false,
          failed: false,
          modified: new Date(payloadData?.modified ?? Date.now()).getTime() || Date.now()
        },
        state
      )
    }
    case actionTypes.NODES.ONE.CREATE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.data = state.syncData
        failedState.failed = false
      }
      return failedState
    }
    case actionTypes.NODES.ONE.EDIT.REQUEST: {
      let data: Partial<NodeDataState> = {}
      if (typeof action.payload?.content === 'string') data.content = action.payload.content
      if (typeof action.payload?.kind === 'string') data.kind = action.payload.kind
      if (action.meta?.isRetry) data = {}
      return defaultsDeep(
        {
          data: data,
          isFetching: true
        },
        state
      )
    }
    case actionTypes.NODES.ONE.EDIT.RECEIVE:
    case actionTypes.NODES.ONE.EDIT.NOTIFY: {
      const payloadData = action.payload?.data
      const data = defaultsDeep(
        {
          content: payloadData?.content,
          kind: payloadData?.kind
        },
        state.data
      )
      return defaultsDeep(
        {
          syncData: data,
          data: data,
          isFetching: false,
          failed: false,
          modified: new Date(payloadData?.modified ?? Date.now()).getTime() || Date.now()
        },
        state
      )
    }
    case actionTypes.NODES.ONE.EDIT.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.data = state.syncData
        failedState.failed = false
      }
      return failedState
    }
    case actionTypes.NODES.ONE.DELETE.REQUEST:
      return defaultsDeep(
        {
          isFetching: true,
          deleted: true
        },
        state
      )
    case actionTypes.NODES.ONE.DELETE.RECEIVE:
    case actionTypes.NODES.ONE.DELETE.NOTIFY: {
      const update: Partial<NodeState> = { isFetching: false }
      if (!state.local) update.deleted = true
      update.modified = Date.now()
      if (action.payload?.data?.modified) update.modified = new Date(action.payload.data.modified).getTime()
      return defaultsDeep(update, state)
    }
    case actionTypes.NODES.ONE.DELETE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.deleted = false
        failedState.failed = false
      }
      return failedState
    }
    case actionTypes.NODES.MULTI.DELETE.REQUEST:
      return defaultsDeep(
        {
          isFetching: true,
          deleted: true
        },
        state
      )
    case actionTypes.NODES.MULTI.DELETE.RECEIVE:
    case actionTypes.NODES.MULTI.DELETE.NOTIFY: {
      const update: Partial<NodeState> = { isFetching: false }
      if (!state.local) update.deleted = true
      update.modified = Date.now()
      return defaultsDeep(update, state)
    }
    case actionTypes.NODES.MULTI.DELETE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.deleted = false
        failedState.failed = false
      }
      return failedState
    }
    default:
      return state
  }
}

export default single
